/**
 * FiledReportDetail — BUI-91: the NATIVE in-app view of a filed report. Built on `FiledReport`
 * — the server's real folder listing — and NOT on `ReportEntry`. Re-opening
 * a filed report renders its DATA with the app's own components (the
 * `ReportContents` below), not the PDF: the report's content is already
 * structured records, so rendering it natively is less work than a PDF
 * surface and looks like the app rather than a paper page on a phone. The
 * PDF stays as the durable export/share artifact in the folder (lifecycle
 * step 1, Red 7/31) — it is what leaves the app, not what the app fails to
 * show.
 */

import { BackLink } from "./BackLink";
import { ReportContents } from "./ReportContents";
import type { FiledReport } from "../reports/report-client";
import { KIND_LABEL, clientBlocked, filedAtLabel, sizeLabel, titleOf } from "../reports/report-labels";
import type { ReportEntry, ReportKind, ReportSidecar } from "../reports/report-types";

export interface FiledReportDetailProps {
  filedDetail: FiledReport;
  sidecar: ReportSidecar | null;
  sidecarError: string | null;
  closeFiledDetail: () => void;
  openRefilePreview: (report: FiledReport) => Promise<void>;
  fetchPhoto?: (media_ref: string) => Promise<Blob | null>;
  onOpenItem?: (capture_id: string) => void;
  onDownload?: (report: FiledReport) => void;
}

export function FiledReportDetail({
  filedDetail,
  sidecar,
  sidecarError,
  closeFiledDetail,
  openRefilePreview,
  fetchPhoto,
  onOpenItem,
  onDownload,
}: FiledReportDetailProps) {
  return (
    <div data-testid="report-filed-detail">
      <BackLink label="Reports" testid="report-filed-detail-back" onBack={closeFiledDetail} />
      <p class="report-detail-title" data-testid="report-filed-detail-title">
        {titleOf({
          report_kind: (filedDetail.kind as ReportKind) ?? "daily",
          window_key: `${filedDetail.kind}-${filedDetail.date_slug}`,
        } as ReportEntry)}
      </p>
      <p class="report-detail-meta" data-testid="report-filed-detail-meta">
        {[
          `${KIND_LABEL[filedDetail.kind as ReportKind] ?? filedDetail.kind} · ${filedDetail.date_slug}`,
          filedDetail.revision && `Revision ${filedDetail.revision_number ?? 1}`,
          filedAtLabel(filedDetail.filed_at) && `Filed ${filedAtLabel(filedDetail.filed_at)}`,
          sizeLabel(filedDetail.bytes),
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>

      {/* The report itself, rendered natively. report_digest writes a JSON
          sidecar next to every PDF holding the exact ops the report was built
          from; this screen reads that and renders it with the same components
          the rest of the app uses (responsive, dark-mode-aware). The one
          question a super has about a filed report ("what did this say?") is
          answered in the app, not only in the folder. */}
      <ReportContents
        prefix="report-filed-detail"
        heading="In this report"
        whereFallback={`Mark can't read this one back right now. The filed PDF is still in the ${filedDetail.bucket} folder.`}
        sidecar={sidecar}
        sidecarError={sidecarError}
        onOpenItem={onOpenItem}
        fetchPhoto={fetchPhoto}
      />

      {/* The PDF is the EXPORT — the durable, shareable copy a human attaches
          to an email (Red, 2026-07-27). It is not what you read in the app; the
          native view above is. This line points at it as an artifact, not as a
          viewer Mark is missing. */}
      <p class="report-detail-meta" data-testid="report-filed-detail-where">
        Exported as a PDF in the {filedDetail.bucket} folder — download it to share it or
        attach it to an email.
      </p>
      <button
        type="button"
        class="report-file-action"
        data-testid="report-download"
        onClick={() => onDownload?.(filedDetail)}
      >
        Download PDF
      </button>

      {/* BUI-91 lifecycle step 3: Edit → re-export. Editing a filed report
          re-opens the same in-app report screen the window was filed from and,
          on save, emits a new immutable revision for that window — so anything
          captured since is picked up without erasing history. §4.7 / rule #7 still holds:
          weekly takes its second confirmation when the refreshed projection is
          approved, not merely when the human asks to edit it. */}
      <button
        type="button"
        class="report-file-action"
        data-testid="report-refile"
        disabled={filedDetail.is_head === false}
        aria-disabled={clientBlocked(filedDetail.kind) || filedDetail.is_head === false}
        onClick={() => void openRefilePreview(filedDetail)}
      >
        {filedDetail.is_head === false
            ? "Historical revision · view only"
            : "Edit & re-export"}
      </button>
      <p class="report-detail-meta" data-testid="report-refile-hint">
        {!filedDetail.artifact_id
          ? "Editing preserves this PDF byte-for-byte and files the current contents as the first governed successor."
          : filedDetail.is_head === false
            ? "This immutable generation has a newer revision and cannot branch. Open the newest generation to edit and re-export."
            :
              "Editing re-opens this report for the same window, preserves this version, and files a new revision so anything captured since is included."}
      </p>
    </div>
  );
}
